import { FileText, AlertTriangle, ShieldAlert } from "lucide-react";

const CARDS = [
  {
    icon: FileText,
    title: "What Is a Trademark?",
    content:
      "A trademark is a legally registered symbol, word, phrase, logo, design, or combination thereof that identifies and distinguishes the source of goods or services of one entity from those of others. In the United States, trademarks are registered through the United States Patent and Trademark Office (USPTO). Once registered, a trademark grants the owner exclusive rights to use the mark nationwide in connection with the goods or services listed in the registration. Trademarks can include brand names, slogans, product names, logos, and even sounds or colors that are uniquely associated with a business. Federal trademark registration provides legal presumption of ownership, the right to use the registered symbol, and the ability to bring legal action in federal court against infringers.",
  },
  {
    icon: AlertTriangle,
    title: "Why Register Your Trademark?",
    content:
      "Registering your trademark with the USPTO is one of the most important steps you can take to protect your brand identity and business reputation. Without federal registration, your rights are limited to the geographic area where you actually use the mark, leaving your brand vulnerable to competitors who may adopt similar names, logos, or slogans in other parts of the country. A registered trademark provides nationwide priority, acts as constructive notice to others that the mark is taken, and serves as a powerful deterrent against would-be infringers. Additionally, a federally registered trademark is required to list your brand on platforms like Amazon Brand Registry, and it significantly strengthens your position in any trademark dispute or litigation.",
  },
  {
    icon: ShieldAlert,
    title: "Risks of Not Registering",
    content:
      "Operating without a registered trademark exposes your business to significant risks. Competitors may adopt confusingly similar brand names, logos, or slogans, diluting your brand identity and potentially diverting your customers. Without federal registration, you have no legal presumption of ownership, making it far more difficult and expensive to enforce your rights in court. You also risk losing the ability to use your own brand name if another party registers a similar mark before you do. Counterfeit products and unauthorized use of your brand can damage your reputation, erode consumer trust, and result in substantial financial losses. The cost of trademark litigation far exceeds the cost of registration, making proactive protection the smarter investment for any business owner.",
  },
];

export default function OverviewSection() {
  return (
    <section
      id="overview"
      data-testid="overview-section"
      className="py-20 md:py-32 bg-[#F8F9FA]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#1E3A8A] mb-4">
            Trademark Registration Overview
          </span>
          <h2
            data-testid="overview-heading"
            className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0F172A] tracking-tight mb-4"
          >
            Understanding Trademark Registration in the United States
          </h2>
          <p className="text-[#475569] text-base sm:text-lg max-w-3xl mx-auto leading-relaxed">
            Trademark registration is a critical step in building and maintaining a successful brand. Learn why securing your intellectual property through the USPTO is essential for long-term business success and brand protection across all 50 states.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {CARDS.map((card, i) => (
            <div
              key={card.title}
              data-testid={`overview-card-${i}`}
              className="bg-white border border-slate-200 rounded-lg p-8 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300"
            >
              <div className="w-12 h-12 rounded-lg bg-[#1E3A8A]/10 flex items-center justify-center mb-5">
                <card.icon className="w-6 h-6 text-[#1E3A8A]" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-[#0F172A] mb-4">
                {card.title}
              </h3>
              <p className="text-[#475569] text-sm leading-relaxed">{card.content}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
